import { useLayoutEffect, type RefObject } from 'react';

// Bottom sheets above the software keyboard (iPhone real-photo run 2026-10-26, #6: the city sheet's 「完成」 sat
// under the iOS keyboard's accessory bar). WKWebView does not resize the page for the keyboard: the layout
// viewport keeps its height and only the VISUAL viewport — what the person sees — gets shorter (and may be
// scrolled within the layout viewport). A position:fixed backdrop is laid out against the layout viewport, so
// a bottom sheet's footer ends up behind the keys. While the visual viewport is shorter than the backdrop, the
// backdrop is pinned to the visible band instead (top = its offset, height = its height) and the sheet is
// capped to that band, so the sheet's bottom — its footer and Done — rests right above the keyboard.
//
// Nothing changes where the page already resizes for the keyboard (Android WebView with adjustResize) or there
// is no keyboard (desktop): the two heights are then equal. A pinch-zoomed page (scale ≠ 1) is left alone.
export interface VisualViewportLike {
  height: number;
  offsetTop: number;
  scale?: number;
}

export interface VisibleBand {
  top: number;
  height: number;
}

// The visible part of a box that fills the layout viewport, or null when all of it is visible.
export function visibleBand(layoutHeight: number, viewport: VisualViewportLike | null | undefined): VisibleBand | null {
  if (!viewport || !(layoutHeight > 0) || !(viewport.height > 0)) return null;
  if (viewport.scale !== undefined && Math.abs(viewport.scale - 1) > 0.01) return null;
  const top = Math.max(0, viewport.offsetTop);
  const height = Math.min(viewport.height, layoutHeight - top);
  if (top < 1 && height >= layoutHeight - 1) return null;
  return { top: Math.round(top), height: Math.round(height) };
}

const BACKDROP_PROPS = ['top', 'height', 'bottom'] as const;

export function useVisibleViewportFit(backdrop: RefObject<HTMLElement | null>, panel: RefObject<HTMLElement | null>, enabled: boolean): void {
  useLayoutEffect(() => {
    const viewport = typeof window !== 'undefined' ? window.visualViewport : null;
    const box = backdrop.current;
    if (!enabled || !viewport || !box) return undefined;
    const reset = () => {
      for (const prop of BACKDROP_PROPS) box.style[prop] = '';
      if (panel.current) panel.current.style.maxHeight = '';
      delete box.dataset.keyboard;
    };
    const apply = () => {
      // Measure the backdrop as CSS lays it out (the whole layout viewport), then pin it to what is visible.
      reset();
      const band = visibleBand(box.offsetHeight, viewport);
      if (!band) return;
      box.style.top = `${band.top}px`;
      box.style.height = `${band.height}px`;
      box.style.bottom = 'auto';
      box.dataset.keyboard = 'true';
      // The sheet never reaches above the visible band (nor under the status bar at its top).
      if (panel.current) panel.current.style.maxHeight = `calc(${band.height}px - env(safe-area-inset-top, 0px) - 8px)`;
    };
    apply();
    viewport.addEventListener('resize', apply);
    viewport.addEventListener('scroll', apply);
    return () => {
      viewport.removeEventListener('resize', apply);
      viewport.removeEventListener('scroll', apply);
      reset();
    };
  }, [backdrop, panel, enabled]);
}
